/**
 * Pattern Recognition Service
 * 
 * Analyzes historical transaction data to identify patterns and generate
 * classification rules automatically.
 */

interface Transaction {
  date: string;
  counterparty: string;
  description: string;
  amount: number;
  debitAccount?: string;
  creditAccount?: string;
  vatRate?: number;
}

interface Pattern {
  id: string;
  type: 'counterparty' | 'description' | 'amount_range';
  key: string;
  debitAccount: string;
  creditAccount: string;
  vatRate: number;
  frequency: number;
  consistency: number; // 0-100%
  confidence: number; // 0-100%
  lastSeen: string;
  samples: Transaction[];
}

interface GeneratedRule {
  patternKey: string;
  patternType: 'counterparty' | 'description' | 'amount_range';
  suggestedDebitAccount: string;
  suggestedCreditAccount: string;
  vatCode: string;
  confidence: number;
  frequency: number;
  descriptionTemplate: string;
  reasoning: string;
}

interface AnalysisResult {
  totalTransactions: number;
  analyzedTransactions: number;
  patternsFound: number;
  rulesGenerated: number;
  patterns: Pattern[];
  suggestedRules: GeneratedRule[];
  avgConfidence: number;
  processingTimeMs: number;
}

export class PatternRecognitionService {
  /**
   * Analyze historical transactions to identify patterns
   */
  static async analyzeHistoricalData(
    transactions: Transaction[]
  ): Promise<AnalysisResult> {
    const startTime = Date.now();
    
    // Filter transactions that have account classifications
    const classifiedTransactions = transactions.filter(
      t => t.debitAccount && t.creditAccount
    );

    if (classifiedTransactions.length === 0) {
      return {
        totalTransactions: transactions.length,
        analyzedTransactions: 0,
        patternsFound: 0,
        rulesGenerated: 0,
        patterns: [],
        suggestedRules: [],
        avgConfidence: 0,
        processingTimeMs: Date.now() - startTime,
      };
    }

    // Group transactions by counterparty
    const counterpartyGroups = this.groupByCounterparty(classifiedTransactions);
    
    // Identify patterns with high frequency and consistency
    const patterns = this.identifyPatterns(counterpartyGroups);
    
    // Generate rules from high-confidence patterns
    const suggestedRules = this.generateRules(patterns);
    
    // Calculate average confidence
    const avgConfidence = suggestedRules.length > 0
      ? suggestedRules.reduce((sum, r) => sum + r.confidence, 0) / suggestedRules.length
      : 0;

    return {
      totalTransactions: transactions.length,
      analyzedTransactions: classifiedTransactions.length,
      patternsFound: patterns.length,
      rulesGenerated: suggestedRules.length,
      patterns,
      suggestedRules,
      avgConfidence,
      processingTimeMs: Date.now() - startTime,
    };
  }

  /**
   * Group transactions by counterparty
   */
  private static groupByCounterparty(
    transactions: Transaction[]
  ): Map<string, Transaction[]> {
    const groups = new Map<string, Transaction[]>();
    
    transactions.forEach(txn => {
      const key = this.normalizeCounterparty(txn.counterparty);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(txn);
    });
    
    return groups;
  }

  /**
   * Normalize counterparty name for matching
   */
  private static normalizeCounterparty(counterparty: string): string {
    return counterparty
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[^a-z0-9\s]/g, '');
  }

  /**
   * Identify patterns from grouped transactions
   */
  private static identifyPatterns(
    groups: Map<string, Transaction[]>
  ): Pattern[] {
    const patterns: Pattern[] = [];
    
    groups.forEach((transactions, counterparty) => {
      // Need at least 3 occurrences to consider it a pattern
      if (transactions.length < 3) return;
      
      // Check if debit/credit accounts are consistent
      const accountPairs = transactions.map(t => ({
        debit: t.debitAccount!,
        credit: t.creditAccount!,
        vat: t.vatRate || 0,
      }));
      
      // Find most common account pair
      const pairCounts = new Map<string, { count: number; debit: string; credit: string; vat: number }>();
      accountPairs.forEach(pair => {
        const key = `${pair.debit}|${pair.credit}|${pair.vat}`;
        if (!pairCounts.has(key)) {
          pairCounts.set(key, { count: 0, debit: pair.debit, credit: pair.credit, vat: pair.vat });
        }
        pairCounts.get(key)!.count++;
      });
      
      // Get most common pair
      let maxCount = 0;
      let mostCommonPair: { debit: string; credit: string; vat: number } | null = null;
      pairCounts.forEach(value => {
        if (value.count > maxCount) {
          maxCount = value.count;
          mostCommonPair = { debit: value.debit, credit: value.credit, vat: value.vat };
        }
      });
      
      if (!mostCommonPair) return;
      
      // Calculate consistency (what % of transactions use this account pair)
      const consistency = (maxCount / transactions.length) * 100;
      
      // Only consider patterns with >80% consistency
      if (consistency < 80) return;
      
      // Calculate confidence based on frequency and consistency
      const frequency = transactions.length;
      const confidence = Math.min(
        95,
        50 + (frequency * 2) + (consistency / 2)
      );
      
      patterns.push({
        id: `pattern-${counterparty}`,
        type: 'counterparty',
        key: counterparty,
        debitAccount: mostCommonPair.debit,
        creditAccount: mostCommonPair.credit,
        vatRate: mostCommonPair.vat,
        frequency,
        consistency,
        confidence,
        lastSeen: transactions[transactions.length - 1].date,
        samples: transactions.slice(0, 3), // Keep first 3 as samples
      });
    });
    
    // Sort by confidence descending
    return patterns.sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * Generate rules from identified patterns
   */
  private static generateRules(patterns: Pattern[]): GeneratedRule[] {
    return patterns
      .filter(p => p.confidence >= 85) // Only high-confidence patterns
      .map(pattern => ({
        patternKey: pattern.key,
        patternType: pattern.type,
        suggestedDebitAccount: pattern.debitAccount,
        suggestedCreditAccount: pattern.creditAccount,
        vatCode: this.formatVatCode(pattern.vatRate),
        confidence: pattern.confidence,
        frequency: pattern.frequency,
        descriptionTemplate: `${pattern.key} transaction`,
        reasoning: `Used ${pattern.frequency} times with ${pattern.consistency.toFixed(0)}% consistency`,
      }));
  }

  /**
   * Format VAT rate as VAT code
   */
  private static formatVatCode(vatRate: number): string {
    if (vatRate === 0) return '0';
    if (vatRate === 2.5) return '2.5';
    if (vatRate === 3.7) return '3.7';
    if (vatRate === 7.7 || vatRate === 8.1) return '8.1';
    return '0';
  }

  /**
   * Parse CSV/Excel/PDF historical data
   * PDFs are processed server-side via Edge Function
   */
  static async parseHistoricalFile(file: File): Promise<Transaction[]> {
    const fileType = file.name.toLowerCase();
    
    // Handle PDF files via Edge Function
    if (fileType.endsWith('.pdf')) {
      console.log('[Parser] PDF detected, sending to Edge Function for processing');
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        // Call Supabase Edge Function
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-historical-pdf`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: formData,
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.errors?.[0] || 
            `PDF processing failed: ${response.statusText}`
          );
        }
        
        const result = await response.json();
        
        if (!result.success || !result.transactions) {
          throw new Error(result.errors?.[0] || 'Failed to parse PDF');
        }
        
        console.log(`[Parser] Successfully parsed ${result.totalParsed} transactions from PDF`);
        return result.transactions;
        
      } catch (error) {
        console.error('[Parser] PDF parsing error:', error);
        throw new Error(
          `Failed to process PDF: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    }
    
    // Handle CSV/Excel files (browser-side parsing)
    console.log('[Parser] CSV/Excel detected, parsing in browser');
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
      throw new Error('File must contain header and at least one transaction');
    }
    
    // Parse header
    const header = lines[0].toLowerCase().split(/[,;|\t]/);
    const dateIdx = this.findColumnIndex(header, ['date', 'datum', 'transaction date']);
    const counterpartyIdx = this.findColumnIndex(header, ['counterparty', 'gegenpartei', 'name', 'description']);
    const amountIdx = this.findColumnIndex(header, ['amount', 'betrag', 'menge']);
    const debitIdx = this.findColumnIndex(header, ['debit', 'soll', 'debit account', 'sollkonto']);
    const creditIdx = this.findColumnIndex(header, ['credit', 'haben', 'credit account', 'habenkonto']);
    const vatIdx = this.findColumnIndex(header, ['vat', 'mwst', 'tax', 'vat rate']);
    
    if (dateIdx === -1 || counterpartyIdx === -1 || amountIdx === -1) {
      throw new Error('Missing required columns: Date, Counterparty, Amount');
    }
    
    // Parse transactions
    const transactions: Transaction[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(/[,;|\t]/);
      
      const transaction: Transaction = {
        date: cols[dateIdx]?.trim() || '',
        counterparty: cols[counterpartyIdx]?.trim() || '',
        description: cols[counterpartyIdx]?.trim() || '',
        amount: parseFloat(cols[amountIdx]?.replace(/[^0-9.-]/g, '') || '0'),
      };
      
      if (debitIdx !== -1) {
        transaction.debitAccount = cols[debitIdx]?.trim();
      }
      if (creditIdx !== -1) {
        transaction.creditAccount = cols[creditIdx]?.trim();
      }
      if (vatIdx !== -1) {
        const vatStr = cols[vatIdx]?.replace(/[^0-9.]/g, '') || '0';
        transaction.vatRate = parseFloat(vatStr);
      }
      
      transactions.push(transaction);
    }
    
    return transactions;
  }

  /**
   * Find column index by possible names
   */
  private static findColumnIndex(header: string[], possibleNames: string[]): number {
    for (const name of possibleNames) {
      const idx = header.findIndex(col => col.includes(name));
      if (idx !== -1) return idx;
    }
    return -1;
  }
}
